import Image from "next/image";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import InterfaceControls from "~/components/home/interface-controls";
import { RoomList, SearchBar } from "~/components/home/room-list";
import { CreateRoomModal } from "~/components/modals/create-room-modal";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { useI18nContext } from "~/i18n/i18n-react";
import useAuthStore from "~/stores/auth";
import { api } from "~/utils/api";
import { preloadCallLayout } from "./room/[id]";
import dynamic from "next/dynamic";
import { redirectIfMobile } from "~/utils/redirect-if-mobile";
import { GetServerSideProps, GetServerSidePropsContext } from "next";

const SocketGlobalRoom = dynamic(
	() => import("~/components/home/socket-global-room"),
	{
		loading: () => null,
		ssr: false,
	},
);

export default function Home() {
	const [search, setSearch] = useState<string | undefined>(undefined);
	const { LL } = useI18nContext();
	const { theme } = useTheme();
	const isLight = theme === "light";
	const { user } = useAuthStore();

	const name = user?.name?.split(" ")[0] ?? "";

	const { data, isInitialLoading: isRoomsLoading } = api.room.list.useQuery(
		{
			limit: 10,
			offset: 0,
			query: search,
		},
		{ getNextPageParam: (lastPage) => lastPage.nextOffset },
	);

	const onSearch = ({ search }: { search: string }) => {
		setSearch(search);
	};

	useEffect(() => {
		void preloadCallLayout();
	}, []);

	return (
		<>
			<div className="relative flex h-[100dvh] flex-col items-center justify-center md:h-[94vh] md:flex-row md:gap-10">
				<InterfaceControls />
				<main className="flex h-full w-full flex-col justify-center p-2 md:h-auto md:max-w-lg md:gap-5">
					<div className="flex flex-col justify-center gap-1 p-0 text-start">
						<p className="whitespace-nowrap text-3xl font-bold capitalize">
							{LL.WELCOME({ name })}
						</p>
						<div className="text-lg text-secondary-foreground">
							{LL.JOIN_ROOM_DESCRIPTION()}
						</div>
					</div>
					<div className="flex w-full flex-1 flex-col items-center justify-center gap-4">
						<SearchBar onSearch={onSearch} />
						<RoomList rooms={data?.rooms} isLoading={isRoomsLoading} />
					</div>
				</main>
				<div className="hidden w-full flex-col items-center justify-center gap-6 py-14 md:order-last md:flex md:max-w-lg md:py-0">
					<Image
						priority
						src={"/create-room-art.svg"}
						alt=""
						className="h-24 w-auto"
						width="72"
						height="70"
					/>
					<div className="justify-center text-center text-lg text-secondary-foreground md:whitespace-nowrap">
						{LL.NO_ROOM()}
					</div>
					<CreateRoomModal>
						<Button className="w-80">
							{"+ "}
							{LL.CREATE_ROOM()}
						</Button>
					</CreateRoomModal>
				</div>
				<Separator
					orientation="vertical"
					className="hidden h-4/5 bg-foreground/20 md:block"
				/>
			</div>
			<Image
				className="mx-auto mt-auto hidden h-auto w-72 md:flex"
				src={isLight ? "/credits.svg" : "/credits-dark.svg"}
				alt="Powered by Tech Unicorn credits"
				width={249}
				height={25}
			/>
			<SocketGlobalRoom />
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (
	context: GetServerSidePropsContext,
) => {
	const redirect = redirectIfMobile(context.req);
	if (redirect) return redirect;

	return { props: {} };
};
